/** @jsxImportSource @emotion/react */
import { useEffect, useState } from "react";
import { accessAPI, Boton, Titulo, downloadFile, formatMonto, useIva } from "../../shared";
import { descripcionFactura, fechaCorta, haceDias, montoFactura } from "../../portal/utilidades";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";

// Lo que requiere acción del cliente: hallazgos a responder, facturas
// impagas y propuestas por aceptar (cliente/destacados). Orden: hallazgos,
// facturas, propuestas comerciales y sábana, igual que los Destacados de antes.
// `soloHallazgos`: solo los hallazgos a responder (secciones Hallazgos y
// actividad); si no hay ninguno la card no se muestra.
// `actividadId`: con soloHallazgos, solo los de esa actividad.
// `urlVolver`: la pantalla en la que está la card, para el ?volver= de sus
// links (la flecha de la pantalla de destino trae de vuelta acá)
// Colores de las sub-cards (antes en portal.css)
const TEXTO = "rgba(44, 44, 44, 1)";
const BORDE = "1px solid #e0e0e0";

// Grilla de dos columnas; una sola en mobile
const grilla = {
  display: "grid",
  gap: "1em",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  "@media (max-width: 640px)": { gridTemplateColumns: "minmax(0, 1fr)" }
};

export default function RequiereAtencion({
  soloHallazgos = false,
  actividadId,
  urlVolver = "/"
}) {
  // Lo que requiere atención del cliente (cliente/destacados). Se pide al
  // montarse: la card está en el Inicio, Hallazgos y cada actividad
  const [destacados, setDestacados] = useState(null);
  useEffect(() => {
    accessAPI(
      "GET",
      "cliente/destacados",
      null,
      (r) =>
        setDestacados({
          propuestas: r.propuestas || [],
          sabanas: r.sabanas || [],
          hallazgos: r.hallazgos || [],
          facturas: r.facturas || []
        }),
      () => setDestacados({ propuestas: [], sabanas: [], hallazgos: [], facturas: [] })
    );
  }, []);
  const totalPendientes = destacados
    ? destacados.propuestas.length + destacados.sabanas.length + destacados.hallazgos.length + destacados.facturas.length
    : 0;
  const hallazgos = (destacados?.hallazgos || []).filter(
    (h) => !actividadId || h.actividadId === Number(actividadId)
  );
  const cantidad = soloHallazgos ? hallazgos.length : totalPendientes;

  if (soloHallazgos && destacados && cantidad === 0) return null;

  return (
    <Card>
      <Titulo
        etiquetas={
          cantidad > 0 && [{ texto: `${cantidad} pendientes`, error: true }]
        }
      >
        <i className="pi pi-bell" css={{ marginRight: "0.5em" }} />
        Requiere tu atención
      </Titulo>
      {/* Sub-cards de pendientes, en la grilla de dos columnas */}
      {!destacados && (
        <div css={grilla}>
          {[...Array(2)].map((_, i) => (
            <Skeleton key={i} height="9rem" borderRadius="10px" />
          ))}
        </div>
      )}
      {destacados && cantidad === 0 && (
        <div css={{ textAlign: "center", padding: "2em 1em", color: "var(--grisTextos)" }}>
          <i className="pi pi-check-circle" css={{ fontSize: "1.5rem" }} />
          <div css={{ fontWeight: 600, color: "rgba(44, 44, 44, 1)", marginTop: "0.5em" }}>Estás al día</div>
          <p css={{ margin: "0.25em 0 0" }}>No tienes hallazgos por responder, facturas pendientes ni propuestas por aceptar.</p>
        </div>
      )}
      {destacados && cantidad > 0 && (
        <div css={grilla}>
          {/* Prioridad: facturas a pagar, propuestas a aceptar (comerciales y
              sábana) y después hallazgos a responder */}
          {!soloHallazgos && (
            <>
              {destacados.facturas.map((f) => (
                <PendienteFactura key={`f-${f.id}`} factura={f} />
              ))}
              {destacados.propuestas.map((p) => (
                <PendientePropuesta key={`p-${p.id}`} propuesta={p} urlVolver={urlVolver} />
              ))}
              {destacados.sabanas.map((s) => (
                <PendienteSabana key={`s-${s.id}`} sabana={s} urlVolver={urlVolver} />
              ))}
            </>
          )}
          {hallazgos.map((h) => (
            <PendienteHallazgo key={`h-${h.id}`} hallazgo={h} urlVolver={urlVolver} />
          ))}
        </div>
      )}
    </Card>
  );
}

// `severidad`: color del tag de la cabecera (ícono y etiqueta)
function Pendiente({ icono, etiqueta, severidad, derecha, titulo, texto, pie, accion }) {
  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: "0.75rem",
        borderRadius: 10,
        background: "#f8f9fa",
        border: BORDE,
        padding: "1rem",
        height: "100%",
        boxSizing: "border-box"
      }}
    >
      <div>
        <div
          css={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "0.35rem 0.5rem",
            // Si la fecha no entra al lado de la etiqueta, baja a su propio
            // renglón en vez de salirse de la tarjeta (mobile)
            flexWrap: "wrap"
          }}
        >
          <Tag severity={severidad} icon={icono} value={etiqueta} />
          <span css={{ fontSize: "0.75rem", color: "var(--grisTextos)", whiteSpace: "nowrap" }}>{derecha}</span>
        </div>
        <h3 css={{ fontSize: "0.98rem", margin: "0.35rem 0 0.2rem", lineHeight: 1.35, fontWeight: 600 }}>{titulo}</h3>
        {texto && <div css={{ fontSize: "0.82rem", color: "var(--principal)" }}>{texto}</div>}
      </div>
      <div
        css={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "0.4rem 0.5rem",
          // Si el estado y la acción no entran en un renglón, la acción baja
          // en vez de cortarse
          flexWrap: "wrap",
          paddingTop: "0.75rem",
          borderTop: BORDE,
          fontSize: "0.78rem",
          color: "var(--grisTextos)",
          "& .p-button": { flexShrink: 0, marginLeft: "auto" }
        }}
      >
        <span>{pie}</span>
        {accion}
      </div>
    </div>
  );
}

function PendienteHallazgo({ hallazgo, urlVolver }) {
  const tipo = hallazgo.hallazgoTipo?.nombre || "Hallazgo";
  return (
    <Pendiente
      icono="pi pi-exclamation-triangle"
      severidad="warning"
      etiqueta={tipo}
      derecha={`Creado ${haceDias(hallazgo.fechaCreacion)}`}
      titulo={hallazgo.resumen || hallazgo.actividad?.proceso?.nombrefantasia}
      texto={
        <>
          {/* Proceso (E-1002 · nombre), para ubicarlo */}
          {hallazgo.actividad?.proceso &&
            `Proceso ${[hallazgo.actividad.proceso.nombre, hallazgo.actividad.proceso.nombrefantasia]
              .filter(Boolean)
              .join(" · ")}`}
          {hallazgo.actividad?.actividadtipo?.nombre &&
            ` · ${hallazgo.actividad.actividadtipo.nombre}`}
        </>
      }
      pie="Pendiente de respuesta"
      accion={
        <Boton texto chico uri={`/hallazgo/${hallazgo.id}?volver=${urlVolver}`}>
          Responder
        </Boton>
      }
    />
  );
}

function PendienteFactura({ factura }) {
  const iva = useIva();
  const [descargando, setDescargando] = useState(false);
  const { simbolo, monto } = montoFactura(factura, iva);
  return (
    <Pendiente
      icono="pi pi-receipt"
      severidad="secondary"
      etiqueta={factura.numeroFactura || "Factura"}
      derecha={`Emitida el ${fechaCorta(factura.fechaCreacion)}`}
      titulo="Factura pendiente de pago"
      texto={
        <>
          {descripcionFactura(factura)}
          <div
            css={{
              marginTop: "0.25rem",
              color: TEXTO,
              fontWeight: 700,
              fontSize: "0.95rem"
            }}
          >
            {simbolo} {formatMonto(monto)}
            {factura.masIva && (
              <span
                css={{
                  fontWeight: 400,
                  fontSize: "0.78rem",
                  color: "var(--grisTextos)"
                }}
              >
                {" "}
                (IVA incluido)
              </span>
            )}
          </div>
        </>
      }
      // Con PDF, el pie solo lleva el botón de descarga
      pie={factura.idFacturaBiller || factura.archivoFacturaId ? null : "PDF aún no disponible"}
      accion={
        // Se descarga si está en Biller o si el organismo la subió a mano
        (factura.idFacturaBiller || factura.archivoFacturaId) && (
          <Boton
            texto
            chico
            loader={descargando}
            callback={() => {
              setDescargando(true);
              downloadFile(
                `cliente/destacados/factura/${factura.id}/pdf`,
                `Factura-${factura.numeroFactura || factura.id}.pdf`,
                () => setDescargando(false),
                () => setDescargando(false)
              );
            }}
          >
            Descargar PDF
          </Boton>
        )
      }
    />
  );
}

function PendientePropuesta({ propuesta, urlVolver }) {
  return (
    <Pendiente
      icono="pi pi-file-edit"
      // Sin severity: el color del organismo
      etiqueta="Propuesta comercial"
      derecha={`Emitida el ${fechaCorta(propuesta.fechaemision)}`}
      titulo={propuesta.solicitud?.nombrefantasia || "Propuesta comercial"}
      pie="Pendiente de aceptación"
      accion={
        <Boton texto chico uri={`/propuesta/${propuesta.id}?volver=${urlVolver}`}>
          Revisar
        </Boton>
      }
    />
  );
}

function PendienteSabana({ sabana, urlVolver }) {
  return (
    <Pendiente
      icono="pi pi-file-edit"
      severidad="warning"
      etiqueta="Propuesta sábana"
      derecha={`Emitida el ${fechaCorta(sabana.fechaEmision)}`}
      titulo={sabana.nombre || "Propuesta sábana"}
      pie="Pendiente de aceptación"
      accion={
        <Boton texto chico uri={`/propuestasabana/${sabana.id}?volver=${urlVolver}`}>
          Revisar
        </Boton>
      }
    />
  );
}
